import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Pencil } from 'lucide-react';
import ErrorModal from '@/components/ErrorModal';

export default function Venues() {
  const [venues, setVenues] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ country: '', city: '' });
  const [loading, setLoading] = useState(true);

  // Error modal state
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: 'Error',
    message: ''
  });

  const load = () => { api.get('/venues').then(r => setVenues(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleSave = async () => {
    if (!form.country || !form.city) { 
      setErrorModal({
        open: true,
        title: 'Campos requeridos faltantes',
        message: 'Por favor, completa los campos: País y Ciudad.'
      });
      return; 
    }
    try {
      if (editing) { await api.put(`/venues/${editing}`, form); toast.success('Sede actualizada'); }
      else { await api.post('/venues', form); toast.success('Sede creada'); }
      setShowForm(false); setEditing(null); setForm({ country: '', city: '' }); load();
    } catch (err) { 
      const errorMessage = err.response?.data?.detail || 'Error al guardar sede';
      setErrorModal({
        open: true,
        title: editing ? 'Error al actualizar sede' : 'Error al crear sede',
        message: errorMessage
      });
    }
  };

  const handleEdit = (v) => { setEditing(v.id); setForm({ country: v.country, city: v.city }); setShowForm(true); };
  const handleDelete = async (id) => { if (!window.confirm('Eliminar sede?')) return; await api.delete(`/venues/${id}`); toast.success('Sede eliminada'); load(); };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="admin-venues-page">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black gold-text text-center lg:text-left" style={{ fontFamily: 'Montserrat' }}>Sedes (País / Ciudad)</h2>
        <button onClick={() => { setEditing(null); setForm({ country: '', city: '' }); setShowForm(true); }} className="gold-btn px-4 py-2 rounded-lg text-sm flex items-center gap-2" data-testid="create-venue-button">
          <Plus size={16} /> Crear
        </button>
      </div>

      {loading ? <div className="text-center text-white/50 py-10">Cargando...</div> : venues.length === 0 ? (
        <div className="glass-card p-8 text-center text-white/40">No hay sedes registradas</div>
      ) : (
        <div className="space-y-2">
          {venues.map(v => (
            <div key={v.id} className="glass-card p-4 flex items-center justify-between" data-testid={`venue-row-${v.id}`}>
              <div>
                <div className="text-white font-semibold text-sm">{v.city}</div>
                <div className="text-white/40 text-xs">{v.country}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(v)} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-[#F4E39E]"><Pencil size={16} /></button>
                <button onClick={() => handleDelete(v.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#111A30] border-[#F4E39E]/30 text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-[#F4E39E]" style={{ fontFamily: 'Montserrat' }}>{editing ? 'Editar Sede' : 'Crear Sede'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-white/60 text-sm">País</label>
              <input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} data-testid="venue-country-input"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" placeholder="Mexico" />
            </div>
            <div>
              <label className="text-white/60 text-sm">Ciudad</label>
              <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="venue-city-input"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" placeholder="Ciudad de Mexico" />
            </div>
            <button onClick={handleSave} className="gold-btn w-full py-3 rounded-lg font-bold" data-testid="save-venue-button">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <ErrorModal
        open={errorModal.open}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
}
